import React, { useState } from 'react';
import { Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { useAdminSystemTableColumns } from 'api/hooks/use-admin-system-table-columns';
import SystemTableColumnModal from './SystemTableColumnModal';
import SystemTableColumnOptionsModal from './SystemTableColumnOptionsModal';

const SystemTableColumnsList = ({ tableColumnId, filterType }) => {
  const { data: systemTableColumns, isLoading, error } = useAdminSystemTableColumns({ tableColumnId });
  const [showModal, setShowModal] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedSystemColumn, setSelectedSystemColumn] = useState(null);

  // Filter types that support options
  const supportsOptions = (filterType) => {
    return ['select', 'multiselect', 'reference'].includes(filterType);
  };

  // Check if table column filter type supports options
  const tableColumnSupportsOptions = supportsOptions(filterType);

  // Check if any system column has options support (for backward compatibility)
  const hasOptionsSupport = tableColumnSupportsOptions || systemTableColumns?.some(col => supportsOptions(col.table_column_filter_type));

  const handleEdit = (column) => {
    setEditingColumn(column);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingColumn(null);
    setShowModal(true);
  };

  const handleManageOptions = (column) => {
    setSelectedSystemColumn(column);
    setShowOptionsModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingColumn(null);
  };

  const handleCloseOptionsModal = () => {
    setShowOptionsModal(false);
    setSelectedSystemColumn(null);
  };

  if (isLoading) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">Error loading system table columns: {error.message}</Alert>;
  }

  return (
    <div>
      <div className="mb-3">
        <Button onClick={handleCreate} variant="primary" size="sm">
          Add System Table Column
        </Button>
      </div>

      {systemTableColumns && systemTableColumns.length > 0 ? (
        <Table bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>System</th>
              <th>Display Name</th>
              <th>Table Column</th>
              <th>Source Table</th>
              <th>Source Column</th>
              {hasOptionsSupport && <th>Custom Options</th>}
              {hasOptionsSupport && <th>Options Count</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {systemTableColumns.map((column) => {
              return (
                <tr key={column.id}>
                  <td>{column.system_name}</td>
                  <td>{column.display_name}</td>
                  <td>{column.table_column_name}</td>
                  <td>{column.source_table || <span className="text-muted">—</span>}</td>
                  <td>{column.source_column || <span className="text-muted">—</span>}</td>
                  {hasOptionsSupport && (
                    <td>
                      {tableColumnSupportsOptions ? (
                        column.custom_options ? (
                          <Badge bg="success">Yes</Badge>
                        ) : (
                          <Badge bg="secondary">No</Badge>
                        )
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                  )}
                  {hasOptionsSupport && (
                    <td>
                      {tableColumnSupportsOptions ? (
                        <Badge bg="primary">{column.options_count}</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  )}
                  <td>
                    <Button
                      onClick={() => handleEdit(column)}
                      variant="warning"
                      size="sm"
                      className="me-2"
                    >
                      Edit
                    </Button>
                    {tableColumnSupportsOptions && (
                      <Button
                        onClick={() => handleManageOptions(column)}
                        variant="info"
                        size="sm"
                      >
                        Manage Options
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <Alert variant="info">No system table columns found. Add one to get started.</Alert>
      )}

      <SystemTableColumnModal
        show={showModal}
        onHide={handleCloseModal}
        systemTableColumn={editingColumn}
        tableColumnId={tableColumnId}
        filterType={filterType}
      />

      {selectedSystemColumn && (
        <SystemTableColumnOptionsModal
          show={showOptionsModal}
          onHide={handleCloseOptionsModal}
          systemTableColumn={selectedSystemColumn}
        />
      )}
    </div>
  );
};

export default SystemTableColumnsList;
